import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdCampaign, MdLogin, MdDownload } from 'react-icons/md';
import { BsArrowLeft, BsPaperclip } from 'react-icons/bs';
import { usePppApi } from '../services/pppApi';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT');
}

function formatBytes(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap">{value || '—'}</span>
    </div>
  );
}

export default function AvvisoPublicDetailPage() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const api = usePppApi();
  const [avviso, setAvviso]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.getAvvisoPublic(uuid, {
      onSuccess: (data) => { setAvviso(data); setLoading(false); },
      onError: () => { setNotFound(true); setLoading(false); },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MdCampaign size={28} className="text-blue-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Avvisi Pubblici PPP</h1>
            <p className="text-xs text-gray-500">Art. 193 D.lgs 36/2023 — Partenariato Pubblico Privato</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="flex items-center gap-2 text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <MdLogin size={16} /> Accedi per partecipare
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/avvisi')}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <BsArrowLeft /> Tutti gli avvisi
        </button>

        {loading && (
          <div className="text-center py-16 text-gray-400">Caricamento...</div>
        )}

        {notFound && (
          <div className="text-center py-16">
            <p className="text-gray-500">Avviso non trovato o non più disponibile.</p>
            <button onClick={() => navigate('/avvisi')} className="mt-4 text-blue-600 hover:underline text-sm">
              ← Torna alla lista avvisi
            </button>
          </div>
        )}

        {avviso && (
          <div className="space-y-4">
            {/* Titolo */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Pubblicato</span>
                {avviso.sopraSogliaEuropea && (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">Sopra soglia UE</span>
                )}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">{avviso.oggetto}</h2>
              {avviso.procedimentoTitolo && (
                <p className="text-sm text-gray-500">{avviso.procedimentoTitolo}</p>
              )}
            </div>

            {/* Date */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
              <div className="grid grid-cols-2 gap-4">
                <InfoRow label="Pubblicato il" value={formatDate(avviso.dataPubblicazione)} />
                <InfoRow label="Termine presentazione proposte" value={formatDate(avviso.terminePresentazioneProposte)} />
              </div>
            </div>

            {/* Contenuto */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
              <InfoRow label="Descrizione dell'intervento" value={avviso.descrizioneIntervento} />
              <InfoRow label="Requisiti minimi dei proponenti" value={avviso.requisitiMinimi} />
              {avviso.note && <InfoRow label="Note" value={avviso.note} />}
            </div>

            {/* Link ufficiali */}
            {(avviso.urlAmministrazioneTrasparente || avviso.urlBdncp || avviso.urlGuue) && (
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Pubblicazioni ufficiali</h3>
                <div className="space-y-2">
                  {avviso.urlAmministrazioneTrasparente && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-48 shrink-0">Amministrazione Trasparente</span>
                      <a href={avviso.urlAmministrazioneTrasparente} target="_blank" rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline truncate">
                        {avviso.urlAmministrazioneTrasparente}
                      </a>
                    </div>
                  )}
                  {avviso.urlBdncp && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-48 shrink-0">BDNCP</span>
                      <a href={avviso.urlBdncp} target="_blank" rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline truncate">
                        {avviso.urlBdncp}
                      </a>
                    </div>
                  )}
                  {avviso.urlGuue && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-48 shrink-0">GUUE</span>
                      <a href={avviso.urlGuue} target="_blank" rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline truncate">
                        {avviso.urlGuue}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Allegati scaricabili */}
            {avviso.allegati?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
                  Documenti scaricabili
                </h3>
                <div className="divide-y divide-gray-100">
                  {avviso.allegati.map((a) => (
                    <div key={a.id} className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <BsPaperclip className="text-gray-400 shrink-0" />
                        <div className="min-w-0">
                          <span className="text-sm text-gray-900 font-medium">{a.nome}</span>
                          <span className="ml-2 text-xs text-gray-400">{a.fileNome}</span>
                          {a.fileDimensione && (
                            <span className="ml-1 text-xs text-gray-400">({formatBytes(a.fileDimensione)})</span>
                          )}
                        </div>
                      </div>
                      <a
                        href={`/api/v1/public/avvisi/${uuid}/allegati/${a.id}/download`}
                        download={a.fileNome}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 px-3 py-1.5 border border-blue-200 rounded hover:bg-blue-50 shrink-0 ml-4"
                      >
                        <MdDownload size={14} /> Scarica
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA partecipazione */}
            <div className="bg-blue-600 rounded-xl p-6 text-white text-center">
              <p className="font-semibold mb-2">Vuoi presentare una proposta PPP?</p>
              <p className="text-sm text-blue-100 mb-4">
                Accedi con SPID, registra la tua azienda e manifesta interesse per questo avviso.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="bg-white text-blue-600 px-6 py-2 rounded-lg font-medium hover:bg-blue-50 transition-colors"
              >
                Accedi con SPID
              </button>
            </div>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-400">
            Procedura PPP ex art. 193 D.lgs 36/2023 — Codice dei Contratti Pubblici
          </p>
        </div>
      </main>
    </div>
  );
}
